import React from 'react';
import get from 'lodash/get';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ResponsiveBar } from '@nivo/bar';
import styled, { css } from 'styled-components/macro';
import { formatLocale } from 'app/utils/formatLocale';
import {
  lineYearPeriodFilter,
  SwitchMapState,
  transactionTypeAtom,
  TransactionTypeModel,
} from 'app/state/recoil/atoms';
import {
  getMoneyValueWithMetricPrefix,
  getRange,
} from 'app/components/Charts/line/utils';
import { theme } from './theme';

interface Props {
  data: any;
  keys: string[];
  indexBy: string;
}

const BottomTick = (tick: any) => {
  return (
    <g transform={`translate(${tick.x},${tick.y + 22})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          ...theme.axis.ticks.text,
          fontWeight: 500,
          transform: 'rotate(60deg)',
        }}
      >
        {tick.value}
      </text>
    </g>
  );
};

const Tooltip = (tProps: any, transactionType: TransactionTypeModel) => {
  const styles = {
    container: css`
      z-index: 2;
      display: flex;
      color: #2e4063;
      font-size: 12px;
      background: #fff;
      padding: 24px 24px 8px 24px;
      flex-direction: row;
      border-radius: 20px;
      position: absolute;

      box-shadow: 0 1px 14px rgba(0, 0, 0, 0.12);

      > div:first-of-type {
        margin-right: 30px;
      }
      > div:last-of-type {
        text-align: right;
      }
    `,
    column: css`
      gap: 12px;
    `,
    label: css`
      color: #626262;
      font-family: Inter;
      font-style: normal;
      font-weight: bold;
      font-size: 12px;
      line-height: 15px;
      margin-bottom: 16px;
    `,
    value: css`
      color: #2e4063;
    `,
  };

  return (
    <div css={styles.container}>
      <div css={styles.column}>
        <div css={styles.label}>Year</div>
        <div css={[styles.label, styles.value]}>{tProps.data.x}</div>
      </div>

      <div>
        <div css={styles.label}>{transactionType.name}</div>
        <div css={[styles.label, styles.value]}>
          {formatLocale(tProps.data.y)}
        </div>
      </div>
    </div>
  );
};

export const BarChart = (props: Props) => {
  const [active, toggle] = useRecoilState(SwitchMapState);
  const setYearPeriod = useRecoilState(lineYearPeriodFilter)[1];
  const transactionType = useRecoilValue(transactionTypeAtom);

  const colors = { default: '#2E4063', hover: '#30c2b0' };
  const range = getRange(get(props, 'data', []), ['y']);

  function handleClick(point: any) {
    setYearPeriod(point.data.x.toString());
    toggle(!active);
  }

  const CONSTANTS = {
    borderWidth: 1,
    borderColor: '#2E4063',
    margin: { top: 25, right: 0, bottom: 50, left: 100 },
    padding: 0.65,
    axisTop: null,
    axisRight: null,
    axisBottom: {
      tickSize: 0,
      tickPadding: 8,
      tickRotation: 0,
      renderTick: (tick: any) => BottomTick(tick),
    },
    axisLeft: {
      tickSize: 0,
      tickPadding: 20,
      tickRotation: 0,
      format: (v: any) => formatAxisLeft(v),
    },
    labelSkipWidth: 18,
    colors: colors.default,
    enableLabel: false,
    theme,
    tooltip: (t: any) => Tooltip(t, transactionType),
    onMouseEnter: (_: any, event: any) => handleMouseEnter(event),
    onMouseLeave: (_: any, event: any) => handleMouseLeave(event),
    onClick: handleClick,
  };

  // Function specific to this chart, removing the b(ln) and m(ln) becasue of visual design.
  function formatAxisLeft(v: number) {
    const value = getMoneyValueWithMetricPrefix(v, range.index);
    if (range.abbr.includes('LN')) {
      return value + range.abbr.replace('LN', '');
    }
    return value + range.abbr;
  }

  function handleMouseEnter(event: any) {
    event.target.style.cursor = 'pointer'; // eslint-disable-line no-param-reassign
    event.target.style.fill = colors.hover; // eslint-disable-line no-param-reassign
  }

  function handleMouseLeave(event: any) {
    event.target.style.fill = colors.default; // eslint-disable-line no-param-reassign
  }

  const Container = styled.div`
    width: 100%;
    height: 400px;
  `;

  return (
    <Container>
      <ResponsiveBar {...props} {...CONSTANTS} />
    </Container>
  );
};
